// src/oura/merchant/drawers/VisitsDrawer.tsx
//
// "From Google search": the clicks Search Console counted over its window,
// the change on the window before, and the searches that brought them.
// Opened from the "Who came to you" tile on Home. Clicks, never "visits":
// that is what Search Console counts.

import type { ClientStats, ClientTopQuery } from "@service/aeo/clientReview";
import { Drawer, DrawerBlock, DrawerHead, ListRow, TYPE } from "@oura/ui";
import { cn } from "../../lib/cn";

export function VisitsDrawer({
    open,
    onClose,
    search,
    searches,
    appearingFor,
}: {
    open: boolean;
    onClose: () => void;
    /** Null when Search Console is not connected; the drawer is not opened then. */
    search: NonNullable<ClientStats["search"]>;
    searches: ClientTopQuery[] | null;
    /** New searches the site now shows up for: the fallback when there is no list of searches by visits. */
    appearingFor: string[];
}) {
    const change = search.clicksPrevious === null ? null : search.clicks - search.clicksPrevious;
    // The same Search Console count over two windows of the same length: one
    // method, so the change is a change (rule 2).
    const changeText = change === null
        ? null
        : change === 0
            ? `no change on the ${search.windowDays} days before`
            : `${change > 0 ? "up" : "down"} ${Math.abs(change).toLocaleString("en-GB")} on the ${search.windowDays} days before`;
    return (
        <Drawer open={open} onClose={onClose}>
            {/* The change sits with the count it changes, in the header, not loose between the blocks. */}
            <DrawerHead
                title="From Google search"
                status={{
                    label: `Last ${search.windowDays} days`,
                    clock: { text: `${search.clicks.toLocaleString("en-GB")} ${search.clicks === 1 ? "click" : "clicks"}${changeText ? `, ${changeText}` : ""}` },
                }}
            />
            {searches && searches.length > 0 ? (
                <DrawerBlock label={`Top ${searches.length} searches that bring them`}>
                    {searches.map((s) => (
                        <ListRow
                            key={s.query}
                            title={s.query}
                            right={<span className={cn(TYPE.tb, "shrink-0 font-medium tabular-nums")}>{s.clicks.toLocaleString("en-GB")}</span>}
                        />
                    ))}
                </DrawerBlock>
            ) : appearingFor.length > 0 ? (
                <DrawerBlock label="New searches you now show up for">
                    {appearingFor.map((q) => (
                        <ListRow key={q} title={q} />
                    ))}
                </DrawerBlock>
            ) : (
                <DrawerBlock>Google has not reported which searches brought these clicks yet.</DrawerBlock>
            )}
            <p className={cn(TYPE.tm, "m-0 px-1 text-gray-500")}>Counted by Google Search Console: each click is someone going from Google search to your site.</p>
        </Drawer>
    );
}

